import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '@/api/notifications';
import { cn } from '@/lib/cn';

export function NotificationsDropdown({ onNavigate }: { onNavigate?: () => void }) {
  const { data, isLoading } = useNotifications();
  const markOne = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = (data ?? []).slice(0, 10);

  // Auto-mark visible unread items as read after a brief settling delay,
  // so the bell badge updates the moment the user opens the dropdown.
  // We only mark items the user actually has on screen — the badge count
  // for older unread items stays accurate.
  const markedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const unread = items.filter((n) => !n.is_read && !markedRef.current.has(n.id));
    if (unread.length === 0) return;
    const t = setTimeout(() => {
      for (const n of unread) {
        markedRef.current.add(n.id);
        markOne.mutate(n.id);
      }
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div className="w-80 max-h-96 overflow-y-auto custom-scrollbar">
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
        <span className="text-sm font-semibold text-bb-navy">Notifications</span>
        <button
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending || !items.some((n) => !n.is_read)}
          className="text-xs text-bb-navy hover:underline disabled:opacity-40"
        >
          Mark all read
        </button>
      </div>

      {isLoading ? (
        <div className="py-6 text-center text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-sm text-slate-500">You're all caught up.</div>
      ) : (
        <ul>
          {items.map((n) => {
            const handleClick = () => {
              if (!n.is_read) markOne.mutate(n.id);
              onNavigate?.();
            };
            const classes = cn(
              'block w-full text-left px-3 py-2 hover:bg-slate-50 border-l-2',
              n.is_read ? 'border-transparent' : 'border-bb-mint bg-bb-mint/5',
            );
            return (
              <li key={n.id}>
                {n.link ? (
                  <Link to={n.link} onClick={handleClick} className={classes}>
                    <NotificationBody notif={n} />
                  </Link>
                ) : (
                  <button onClick={handleClick} className={classes}>
                    <NotificationBody notif={n} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="px-3 py-2 border-t border-slate-100 text-center">
        <Link
          to="/notifications"
          onClick={onNavigate}
          className="text-sm text-bb-navy hover:underline font-medium"
        >
          View all
        </Link>
      </div>
    </div>
  );
}

function NotificationBody({
  notif,
}: {
  notif: { title: string; body?: string | null; created_at: string };
}) {
  return (
    <>
      <div className="text-sm font-medium text-slate-800">{notif.title}</div>
      {notif.body ? <div className="text-xs text-slate-500 line-clamp-2">{notif.body}</div> : null}
      <div className="text-[10px] text-slate-400 mt-1">
        {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
      </div>
    </>
  );
}
